import { useEffect, useRef, useState } from "react";

export function CountUp({
  value,
  duration = 900,
  isPercent = false,
  prefix = "",
  format,
  children,
}: {
  value: number;
  duration?: number;
  /** When true, `value` is a 0-1 ratio rendered as a percentage (e.g. 0.95 -> "95%"). Plain counts (including 1) are never auto-detected as percentages — that guess was ambiguous and wrong for whole-number KPIs. */
  isPercent?: boolean;
  /** Prepended to the formatted value, e.g. "₹" for a currency KPI. */
  prefix?: string;
  /** Formats each frame's number (e.g. compact money "₹2.50L"); replaces the default number formatting and `prefix`. */
  format?: (n: number) => string;
  children?: (args: { formatted: string; progress: number }) => React.ReactNode;
}) {
  // Honour "reduce motion": show the final figure at once.
  const reduceMotion =
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [display, setDisplay] = useState(0);
  // A new value (a refresh) counts on from what is shown, not back from zero.
  const shownRef = useRef(0);
  const progressRef = useRef(0);
  const startRef = useRef<number | null>(null);
  const raf = useRef<number | undefined>(undefined);

  const finalValue = isPercent ? value * 100 : value;
  const decimals = isPercent || Number.isInteger(finalValue) ? 0 : 1;
  const suffix = isPercent ? "%" : "";

  const formatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  useEffect(() => {
    cancelAnimationFrame(raf.current!);
    startRef.current = null;
    const from = shownRef.current;

    if (reduceMotion || from === finalValue) {
      progressRef.current = 1;
      shownRef.current = finalValue;
      setDisplay(finalValue);
      return;
    }

    const animate = (time: number) => {
      if (!startRef.current) startRef.current = time;

      const progress = Math.min((time - startRef.current) / duration, 1);
      // Ease out, so the figure settles rather than stopping dead.
      const eased = 1 - Math.pow(1 - progress, 3);

      progressRef.current = progress;
      const next = from + (finalValue - from) * eased;
      shownRef.current = next;
      setDisplay(next);

      if (progress < 1) {
        raf.current = requestAnimationFrame(animate);
      }
    };

    raf.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(raf.current!);
  }, [finalValue, duration, reduceMotion]);

  const formatted = format
    ? format(display)
    : `${prefix}${formatter.format(display)}${suffix}`;

  // 👇 If render-prop is used
  if (children) {
    return <>{children({ formatted, progress: progressRef.current })}</>;
  }

  // 👇 Fallback
  return <>{formatted}</>;
}
